import { useMemo } from 'react'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import type { OnSiteClusterData } from '@/types/mapData'
import type { UserLocationWithStatus } from '@/types/movement'

export function computeOnSiteGroups(
    userLocations: UserLocationWithStatus[],
    geofences: LocationCircle[]
): OnSiteClusterData[] {
    const byId = new Map<string, LocationCircle>()
    for (const circle of geofences) byId.set(circle.id, circle)

    const bySite = new Map<string, OnSiteClusterData>()
    for (const user of userLocations) {
        if (!user.visible) continue
        if (user.trackingStatus.status !== 'working_on_site') continue
        const siteId = user.trackingStatus.siteJobId
        if (!siteId) continue
        const circle = byId.get(siteId)
        if (!circle) continue

        let cluster = bySite.get(siteId)
        if (!cluster) {
            cluster = {
                siteJobId: siteId,
                siteJobName: user.trackingStatus.siteJobName ?? circle.jobName,
                center: { latitude: circle.latitude, longitude: circle.longitude },
                members: [],
            }
            bySite.set(siteId, cluster)
        }
        cluster.members.push({
            userId: user.userId,
            userName: user.userName,
            userColor: user.color,
            stationaryDurationMinutes: user.trackingStatus.stationaryDurationMinutes,
        })
    }

    return Array.from(bySite.values())
}

export function useOnSiteGroups(
    userLocations: UserLocationWithStatus[],
    geofences: LocationCircle[]
): OnSiteClusterData[] {
    return useMemo(() => computeOnSiteGroups(userLocations, geofences), [userLocations, geofences])
}
